import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  AlertCircle,
  RefreshCw,
  Shield,
  Key,
  Tag,
  Brain,
  Zap,
  Send,
  Calendar,
  Building2
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../services/api';
import { getCompanyContext } from 'utils/setCompanyContext';
import {
  MOVFIT_DASHBOARD_AUTOMACOES_WEBHOOK,
  formatMesReferencia,
  parseDashboardAutomacoesMovFit
} from '../utils/movfitDashboardAutomacoesWebhook';

/** Visão geral de automações só para empresa MOV Fit (uma empresa selecionada, slug/nome). */
function isMovFitAutomacoesContext() {
  const ctx = getCompanyContext();
  if (!ctx?.companies?.length) return false;
  if (ctx.isGroup || ctx.isConsolidated || ctx.companies.length > 1) return false;
  const c = ctx.companies[0];
  const slug = (c?.slug || '').toLowerCase();
  const name = (c?.name || '').toLowerCase();
  return (
    slug.includes('movfit') ||
    slug.includes('mov-fit') ||
    name.includes('movfit') ||
    name.includes('mov fit')
  );
}

/**
 * Aba "Dashboard" da XT Fibra (mesma regra de `Automacoes.jsx`: slug com "xt").
 * Os cards genéricos da API são omitidos; novas métricas virão de webhook dedicado.
 */
function isXtFibraAutomacoesContext() {
  if (isMovFitAutomacoesContext()) return false;
  const ctx = getCompanyContext();
  if (!ctx?.companies?.length) return false;
  if (ctx.isGroup || ctx.isConsolidated || ctx.companies.length > 1) return false;
  const slug = (ctx.companies[0]?.slug || '').toLowerCase();
  return slug.includes('xt');
}

function objetoTemChavesMetricas(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return (
    'total de fluxos ativos' in obj ||
    'fluxos principais com ia' in obj ||
    'trocas_senha_mensal' in obj
  );
}

/**
 * Webhook pode vir como objeto plano, { data: {...} }, array de blocos, etc.
 * O fallback Laravel (resumo/por_tipo) não contém essas chaves — não confundir com métricas.
 */
function encontrarObjetoMetricasAutomacoes(input, depth = 0) {
  if (input == null || depth > 8) return null;
  if (typeof input !== 'object') return null;

  if (Array.isArray(input)) {
    for (const item of input) {
      const found = encontrarObjetoMetricasAutomacoes(item, depth + 1);
      if (found) return found;
    }
    return null;
  }

  if (objetoTemChavesMetricas(input)) return input;

  const nested = ['data', 'result', 'body', 'output', 'payload'];
  for (const key of nested) {
    if (key in input && input[key] != null && typeof input[key] === 'object') {
      const found = encontrarObjetoMetricasAutomacoes(input[key], depth + 1);
      if (found) return found;
    }
  }

  for (const value of Object.values(input)) {
    if (value != null && typeof value === 'object') {
      const found = encontrarObjetoMetricasAutomacoes(value, depth + 1);
      if (found) return found;
    }
  }

  return null;
}

function extrairPayloadDashboardAutomacoes(payload) {
  if (payload == null) return null;
  const metricas = encontrarObjetoMetricasAutomacoes(payload);
  if (metricas) return metricas;
  return Array.isArray(payload) ? payload[0] : payload;
}

const COBRANCA_FILTRO_TODAS = '__all__';

const DashboardAutomacoes = ({ refreshKey = 0 }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [empresaTick, setEmpresaTick] = useState(0);
  const [cobrancaMetricas, setCobrancaMetricas] = useState(null);
  const [cobrancaLoading, setCobrancaLoading] = useState(false);
  const [cobrancaError, setCobrancaError] = useState(null);
  const [unidadeFiltro, setUnidadeFiltro] = useState(COBRANCA_FILTRO_TODAS);
  const [cobrancaRetryTick, setCobrancaRetryTick] = useState(0);

  useEffect(() => {
    const onCompany = () => setEmpresaTick((t) => t + 1);
    window.addEventListener('company-context-changed', onCompany);
    window.addEventListener('company-changed', onCompany);
    return () => {
      window.removeEventListener('company-context-changed', onCompany);
      window.removeEventListener('company-changed', onCompany);
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [refreshKey, empresaTick]);

  useEffect(() => {
    if (!isMovFitAutomacoesContext()) {
      setCobrancaMetricas(null);
      setCobrancaError(null);
      setCobrancaLoading(false);
      setUnidadeFiltro(COBRANCA_FILTRO_TODAS);
      return undefined;
    }

    let cancelled = false;

    const carregar = async () => {
      setCobrancaLoading(true);
      setCobrancaError(null);
      try {
        const res = await axios.get(MOVFIT_DASHBOARD_AUTOMACOES_WEBHOOK, { timeout: 60000 });
        if (cancelled) return;
        const parsed = parseDashboardAutomacoesMovFit(res?.data);
        if (parsed) {
          setCobrancaMetricas(parsed.cobranca);
          setCobrancaError(null);
        } else {
          setCobrancaMetricas(null);
          setCobrancaError('Resposta do webhook em formato inesperado.');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao carregar dashboard de automações MOV Fit:', err);
          setCobrancaMetricas(null);
          setCobrancaError(
            err.response?.data?.message || err.message || 'Não foi possível carregar os envios.'
          );
        }
      } finally {
        if (!cancelled) setCobrancaLoading(false);
      }
    };

    carregar();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, empresaTick, cobrancaRetryTick]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      if (isMovFitAutomacoesContext()) {
        setDashboardData(null);
        return;
      }
      if (isXtFibraAutomacoesContext()) {
        setDashboardData(null);
        return;
      }
      const ctx = getCompanyContext();
      const params = { action: 'auto-selected' };
      if (ctx?.companies?.length === 1 && ctx.companies[0]?.name) {
        params.empresa = ctx.companies[0].name;
      }

      const response = await api.get('/automacoes/dashboard', { params });
      const payload = response?.data?.data ?? response.data;
      setDashboardData(extrairPayloadDashboardAutomacoes(payload));
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const movFitSimples = isMovFitAutomacoesContext();
  const xtFibraSimples = isXtFibraAutomacoesContext();

  const cobrancaVisao = useMemo(() => {
    if (!cobrancaMetricas) return null;
    const mesLabel = formatMesReferencia(cobrancaMetricas.mes_atual);
    const refMes = cobrancaMetricas.mes_atual;

    if (unidadeFiltro === COBRANCA_FILTRO_TODAS) {
      return {
        mesLabel,
        card1Titulo: 'Envios totais',
        card1Valor: cobrancaMetricas.total_geral,
        card1Sub: 'Templates de cobrança — consolidado',
        card2Titulo: mesLabel ? `Envios no mês (${mesLabel})` : 'Envios no mês atual',
        card2Valor: cobrancaMetricas.total_mes_atual,
        card2Sub: refMes ? `Referência ${refMes}` : 'Conforme retorno do n8n',
        unidadeNome: null
      };
    }

    const u = cobrancaMetricas.unidades.find((x) => x.unidade === unidadeFiltro);
    if (!u) {
      return {
        mesLabel,
        card1Titulo: 'Envios totais (unidade)',
        card1Valor: 0,
        card1Sub: '—',
        card2Titulo: mesLabel ? `Envios no mês (${mesLabel})` : 'Envios no mês atual',
        card2Valor: 0,
        card2Sub: refMes ? `Referência ${refMes}` : '',
        unidadeNome: null
      };
    }

    return {
      mesLabel,
      card1Titulo: 'Envios totais (unidade)',
      card1Valor: u.total_unidade,
      card1Sub: 'Acumulado nesta unidade',
      card2Titulo: mesLabel ? `Envios no mês (${mesLabel})` : 'Envios no mês atual',
      card2Valor: u.total_mes_atual_unidade,
      card2Sub: refMes ? `Referência ${refMes}` : '',
      unidadeNome: u.unidade
    };
  }, [cobrancaMetricas, unidadeFiltro]);

  useEffect(() => {
    if (!cobrancaMetricas || unidadeFiltro === COBRANCA_FILTRO_TODAS) return;
    const ok = cobrancaMetricas.unidades.some((x) => x.unidade === unidadeFiltro);
    if (!ok) setUnidadeFiltro(COBRANCA_FILTRO_TODAS);
  }, [cobrancaMetricas, unidadeFiltro]);

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label="Carregando dashboard"
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {movFitSimples ? (
        <div className="bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
                <Zap className="h-5 w-5 text-[color:var(--accent)]" />
              </div>
              <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">Automações</h3>
            </div>

            <div className="border-t border-[color:var(--border)] pt-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
                <div className="flex gap-3 min-w-0">
                  <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0 h-fit">
                    <Send className="h-5 w-5 text-[color:var(--accent)]" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-[color:var(--text-primary)]">
                      Templates de cobrança
                    </h4>
                    <p className="text-sm text-[color:var(--text-muted)] mt-0.5">
                      Envios registrados pelo fluxo automático
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 shrink-0">
                  <span className="text-xs font-medium text-[color:var(--text-muted)] flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    Visualizar por unidade
                  </span>
                  <select
                    value={unidadeFiltro}
                    onChange={(e) => setUnidadeFiltro(e.target.value)}
                    className="text-sm rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] px-3 py-2 min-w-[200px] max-w-full lg:max-w-[360px] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/30"
                    disabled={cobrancaLoading || !(cobrancaMetricas?.unidades?.length > 0)}
                  >
                    <option value={COBRANCA_FILTRO_TODAS}>Todas as unidades</option>
                    {(cobrancaMetricas?.unidades || []).map((u) => (
                      <option key={u.unidade} value={u.unidade}>
                        {u.unidade}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {cobrancaLoading && (
                <div className="min-h-[160px] flex items-center justify-center py-6">
                  <LoadingSpinner
                    fullScreen={false}
                    className="w-full"
                    cardClassName="max-w-sm w-full"
                    label="Carregando métricas"
                    subtitle="Buscando dados de automações..."
                  />
                </div>
              )}

              {!cobrancaLoading && cobrancaError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/90 dark:bg-red-950/25 p-4 text-center">
                  <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-[color:var(--text-primary)]">{cobrancaError}</p>
                  <button
                    type="button"
                    onClick={() => setCobrancaRetryTick((t) => t + 1)}
                    className="mt-3 inline-flex items-center text-sm px-3 py-1.5 rounded-md bg-[color:var(--accent)] text-white hover:opacity-90"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Tentar novamente
                  </button>
                </div>
              )}

              {!cobrancaLoading && !cobrancaError && cobrancaVisao && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)]/40 p-5 flex justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[color:var(--text-muted)]">
                          {cobrancaVisao.card1Titulo}
                        </p>
                        <p className="mt-2 text-3xl font-bold text-[color:var(--text-primary)] tabular-nums">
                          {Number(cobrancaVisao.card1Valor).toLocaleString('pt-BR')}
                        </p>
                        <p className="mt-2 text-xs text-[color:var(--text-muted)]">{cobrancaVisao.card1Sub}</p>
                      </div>
                      <div className="p-2.5 bg-[color:var(--surface)] rounded-xl shrink-0 self-start border border-[color:var(--border)]">
                        <Send className="h-5 w-5 text-[color:var(--accent)]" />
                      </div>
                    </div>
                    <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)]/40 p-5 flex justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[color:var(--text-muted)]">
                          {cobrancaVisao.card2Titulo}
                        </p>
                        <p className="mt-2 text-3xl font-bold text-[color:var(--text-primary)] tabular-nums">
                          {Number(cobrancaVisao.card2Valor).toLocaleString('pt-BR')}
                        </p>
                        <p className="mt-2 text-xs text-[color:var(--text-muted)]">{cobrancaVisao.card2Sub}</p>
                      </div>
                      <div className="p-2.5 bg-[color:var(--surface)] rounded-xl shrink-0 self-start border border-[color:var(--border)]">
                        <Calendar className="h-5 w-5 text-[color:var(--accent)]" />
                      </div>
                    </div>
                  </div>

                  {cobrancaVisao.unidadeNome && (
                    <p className="mt-4 text-xs text-[color:var(--text-muted)] break-words border border-dashed border-[color:var(--border)] rounded-lg px-3 py-2 bg-[color:var(--surface-muted)]/30">
                      <span className="font-medium text-[color:var(--text-primary)]">Unidade selecionada: </span>
                      {cobrancaVisao.unidadeNome}
                    </p>
                  )}

                  {unidadeFiltro === COBRANCA_FILTRO_TODAS && cobrancaMetricas?.unidades?.length > 0 && (
                    <div className="mt-6">
                      <p className="text-sm font-semibold text-[color:var(--text-primary)] mb-3">
                        Envios no mês por unidade
                      </p>
                      <div className="space-y-3">
                        {(() => {
                          const maxMes = Math.max(
                            1,
                            ...cobrancaMetricas.unidades.map((x) => x.total_mes_atual_unidade)
                          );
                          return cobrancaMetricas.unidades.map((u) => {
                            const pct = Math.min(100, (u.total_mes_atual_unidade / maxMes) * 100);
                            return (
                              <div
                                key={u.unidade}
                                className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2.5"
                              >
                                <div className="flex justify-between gap-2 text-sm min-w-0">
                                  <span
                                    className="text-[color:var(--text-primary)] truncate pr-2"
                                    title={u.unidade}
                                  >
                                    {u.unidade}
                                  </span>
                                  <span className="font-semibold text-[color:var(--text-primary)] tabular-nums shrink-0">
                                    {u.total_mes_atual_unidade.toLocaleString('pt-BR')}
                                  </span>
                                </div>
                                <div className="mt-2 h-1.5 rounded-full bg-[color:var(--surface-muted)] overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-[color:var(--accent)] transition-[width] duration-500"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : xtFibraSimples ? null : (
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 sm:col-span-6 lg:col-span-4 bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Fluxos ativos</p>
                <p className="mt-1 text-2xl font-semibold text-[color:var(--text-primary)]">
                  {dashboardData?.['total de fluxos ativos'] || 0}
                </p>
              </div>
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Zap className="h-4 w-4 text-[color:var(--accent)]" />
              </div>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6 lg:col-span-4 bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Fluxos com IA</p>
                <p className="mt-1 text-2xl font-semibold text-[color:var(--text-primary)]">
                  {dashboardData?.['fluxos principais com ia'] || 0}
                </p>
              </div>
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Brain className="h-4 w-4 text-[color:var(--accent)]" />
              </div>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6 lg:col-span-4 bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Trocas de senha mensal</p>
                <p className="mt-1 text-2xl font-semibold text-[color:var(--text-primary)]">
                  {dashboardData?.trocas_senha_mensal || 0}
                </p>
              </div>
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Key className="h-4 w-4 text-[color:var(--accent)]" />
              </div>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6 lg:col-span-6 bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Acesso remoto mensal</p>
                <p className="mt-1 text-2xl font-semibold text-[color:var(--text-primary)]">
                  {dashboardData?.qtd_mensal_acesso_remoto || 0}
                </p>
              </div>
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Shield className="h-4 w-4 text-[color:var(--accent)]" />
              </div>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6 lg:col-span-6 bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Etiquetas inseridas no OPA</p>
                <p className="mt-1 text-2xl font-semibold text-[color:var(--text-primary)]">
                  {dashboardData?.qtd_etiquetas_inseridas_mensal || 0}
                </p>
              </div>
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Tag className="h-4 w-4 text-[color:var(--accent)]" />
              </div>
            </div>
          </div>
        </div>
      )}

      {!movFitSimples && !xtFibraSimples && (
        <div className="bg-[color:var(--surface)] p-5 rounded-xl border border-[color:var(--border)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
            <h3 className="text-base font-semibold text-[color:var(--text-primary)] flex items-center">
              <Brain className="h-4 w-4 mr-2 text-[color:var(--accent)]" />
              Fluxos com IA Ativos
            </h3>
            <span className="text-xs text-[color:var(--text-muted)]">
              {dashboardData?.['fluxos principais com ia'] || 0} fluxos
            </span>
          </div>
          <div className="divide-y divide-[color:var(--border)] rounded-lg border border-[color:var(--border)] overflow-hidden bg-[color:var(--surface)]">
            {dashboardData?.['nome dos fluxos com ia ativos']?.map((fluxo, index) => (
              <div key={index} className="flex items-center px-4 py-2.5 hover:bg-[color:var(--surface-muted)] transition-colors">
                <div className="flex-shrink-0">
                  <Brain className="h-4 w-4 text-[color:var(--accent)]" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-[color:var(--text-primary)] truncate" title={fluxo}>
                    {fluxo}
                  </p>
                  <p className="text-xs text-[color:var(--text-muted)]">Fluxo com Inteligencia Artificial</p>
                </div>
                <div className="flex-shrink-0 ml-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[color:var(--surface-muted)] text-[color:var(--accent)]">
                    IA Ativo
                  </span>
                </div>
              </div>
            )) || (
              <div className="text-center py-6">
                <Brain className="h-8 w-8 text-[color:var(--text-muted)] mx-auto mb-2" />
                <p className="text-[color:var(--text-muted)]">Nenhum fluxo com IA ativo</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAutomacoes;
